import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw, Send, XCircle, Ban, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SMS Monitoring Dashboard
 * 
 * Simple metrics view for monitoring SMS launch:
 * - Sent/Failed/Skipped/Pending counts
 * - Opt-out count
 * - Duplicate prevention count
 * - Reminders by hour (last 24h)
 * - Cancellation sends
 * - Top failure reasons
 */
const SMSMonitoring = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Check admin access
  useEffect(() => {
    if (user && !['admin', 'scout'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  const fetchData = async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      
      const res = await apiClient.get('/admin/sms-monitoring');
      setData(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch SMS monitoring data:', err);
      toast.error('Failed to load SMS metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <p className="text-zinc-400">Loading SMS metrics...</p>
      </div>
    );
  }

  const { summary, reminders_by_hour, top_failure_reasons, skip_reasons, recent_activity, by_category } = data || {};

  return (
    <div className="min-h-screen bg-zinc-900 text-white">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-zinc-800 border-b border-zinc-700"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto max-w-6xl px-4 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="h-10 w-10 rounded-full text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">SMS Monitoring</h1>
              <p className="text-xs text-zinc-500">
                {lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : 'Loading...'}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="border-zinc-600 text-zinc-300"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Sent"
            value={summary?.sent_count || 0}
            icon={<CheckCircle className="w-5 h-5 text-green-500" />}
            color="green"
          />
          <MetricCard
            label="Failed"
            value={summary?.failed_count || 0}
            icon={<XCircle className="w-5 h-5 text-red-500" />}
            color="red"
            alert={summary?.failed_count > 0}
          />
          <MetricCard
            label="Skipped"
            value={summary?.skipped_count || 0}
            icon={<Ban className="w-5 h-5 text-yellow-500" />}
            color="yellow"
          />
          <MetricCard
            label="Pending"
            value={summary?.pending_count || 0}
            icon={<Clock className="w-5 h-5 text-blue-500" />}
            color="blue"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricCard
            label="Opt-Outs"
            value={summary?.opt_out_count || 0}
            icon={<Ban className="w-5 h-5 text-orange-500" />}
            color="orange"
            small
          />
          <MetricCard
            label="Duplicates Prevented"
            value={summary?.duplicate_prevented_count || 0}
            icon={<CheckCircle className="w-5 h-5 text-purple-500" />}
            color="purple"
            small
          />
          <MetricCard
            label="Cancellation SMS"
            value={summary?.cancellation_sends || 0}
            icon={<AlertTriangle className="w-5 h-5 text-red-400" />}
            color="red"
            small
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Reminders by Hour */}
          <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
            <h3 className="font-semibold text-zinc-200 mb-4">Reminders by Hour (24h)</h3>
            {Object.keys(reminders_by_hour || {}).length > 0 ? (
              <div className="space-y-2">
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = i;
                  const count = reminders_by_hour?.[hour] || 0;
                  const maxCount = Math.max(...Object.values(reminders_by_hour || { 0: 1 }), 1);
                  const width = (count / maxCount) * 100;
                  
                  return (
                    <div key={hour} className="flex items-center gap-2 text-sm">
                      <span className="w-12 text-zinc-500">{hour.toString().padStart(2, '0')}:00</span>
                      <div className="flex-1 bg-zinc-700 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-blue-500 h-full rounded-full transition-all"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <span className="w-8 text-right text-zinc-400">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">No reminders sent yet</p>
            )}
          </div>

          {/* Failure & Skip Reasons */}
          <div className="space-y-4">
            {/* Top Failures */}
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <h3 className="font-semibold text-zinc-200 mb-3">Top Failure Reasons</h3>
              {top_failure_reasons?.length > 0 ? (
                <div className="space-y-2">
                  {top_failure_reasons.map((item) => (
                    <div key={`failure-${item.reason}`} className="flex justify-between items-center text-sm">
                      <span className="text-red-400 truncate flex-1">{item.reason}</span>
                      <span className="text-zinc-400 ml-2">{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No failures - looking good!</p>
              )}
            </div>

            {/* Skip Reasons */}
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <h3 className="font-semibold text-zinc-200 mb-3">Skip Reasons</h3>
              {skip_reasons?.length > 0 ? (
                <div className="space-y-2">
                  {skip_reasons.map((item) => (
                    <div key={`skip-${item.reason}`} className="flex justify-between items-center text-sm">
                      <span className="text-yellow-400 truncate flex-1">{item.reason}</span>
                      <span className="text-zinc-400 ml-2">{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No skipped SMS</p>
              )}
            </div>

            {/* By Category */}
            <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
              <h3 className="font-semibold text-zinc-200 mb-3">Sent by Category (7d)</h3>
              {Object.keys(by_category || {}).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(by_category).map(([cat, count]) => (
                    <div key={cat} className="flex justify-between items-center text-sm">
                      <span className="text-zinc-300">{cat}</span>
                      <span className="text-zinc-400">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No SMS sent yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
          <h3 className="font-semibold text-zinc-200 mb-4">Recent Activity</h3>
          {recent_activity?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-700">
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Category</th>
                    <th className="pb-2">Type</th>
                    <th className="pb-2">Time</th>
                    <th className="pb-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_activity.map((item) => (
                    <tr key={`activity-${item.sent_at}-${item.event_type}`} className="border-b border-zinc-700/50">
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          item.status === 'SENT' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-2 text-zinc-400">{item.category}</td>
                      <td className="py-2 text-zinc-400">{item.event_type}</td>
                      <td className="py-2 text-zinc-500">
                        {item.sent_at ? new Date(item.sent_at).toLocaleTimeString() : '-'}
                      </td>
                      <td className="py-2 text-red-400 truncate max-w-[200px]">
                        {item.last_error || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ label, value, icon, color, alert = false, small = false }) => {
  const colorClasses = {
    green: 'border-green-500/30',
    red: 'border-red-500/30',
    yellow: 'border-yellow-500/30',
    blue: 'border-blue-500/30',
    orange: 'border-orange-500/30',
    purple: 'border-purple-500/30',
  };

  return (
    <div className={`bg-zinc-800 rounded-xl ${small ? 'p-3' : 'p-4'} border ${colorClasses[color]} ${alert ? 'animate-pulse' : ''}`}>
      <div className="flex items-center justify-between">
        <span className={`${small ? 'text-xs' : 'text-sm'} text-zinc-400`}>{label}</span>
        {icon}
      </div>
      <p className={`${small ? 'text-2xl' : 'text-3xl'} font-bold mt-1`}>{value.toLocaleString()}</p>
    </div>
  );
};

export default SMSMonitoring;
